"use client";
import { useEffect, useRef, useState } from "react";
import { useProductSummaries } from "@/hooks/useProductSummaries";
import RecentlyViewedProductCardListClient from "./RecentlyViewedProductCardListClient";
import type { RecentlyViewedProductCardListProps } from "./types";

const DEFAULT_MAX_ITEMS = 10;
const LOADING_TIMEOUT_MS = 5000; // bail out after 5 seconds

export default function RecentlyViewedProductCardList({
  currentProductId,
  maxItems = DEFAULT_MAX_ITEMS,
  displayLimit,
  minItemsToRender = 3,
  title = "Recently viewed",
  filterByPartner,
  recencyDays = 14,
  ...props
}: RecentlyViewedProductCardListProps) {
  const {
    products: allProducts,
    isLoading,
    error,
  } = useProductSummaries({
    limit: maxItems,
  });
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoading) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return; // don't reset loadingTimedOut here
    }
    setLoadingTimedOut(false); // reset when a NEW load starts
    timerRef.current = setTimeout(
      () => setLoadingTimedOut(true),
      LOADING_TIMEOUT_MS,
    );
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading]);

  let products = currentProductId
    ? allProducts.filter((p) => p.productUuid !== currentProductId)
    : allProducts;

  // Filter to items viewed within the last recencyDays days
  const oneDayInMs = 24 * 60 * 60 * 1000;
  const recencyCutoff = Date.now() - recencyDays * oneDayInMs;
  products = products.filter((p) => p.viewedAt >= recencyCutoff);

  if (filterByPartner) {
    const needle = filterByPartner.toLowerCase();
    products = products.filter((p) => {
      const names = Array.isArray(p.partnerName)
        ? p.partnerName
        : [p.partnerName];
      const keys = p.partnerKeys ?? [];
      const partners = Array.isArray(p.partner)
        ? p.partner
        : p.partner
          ? [p.partner]
          : [];
      return [...names, ...keys, ...partners].some((v) =>
        v?.toLowerCase().includes(needle),
      );
    });
  }

  // Cap the final result independently of the API fetch limit
  if (displayLimit !== undefined) {
    products = products.slice(0, displayLimit);
  }

  // Still fetching and haven't timed out — don't render yet
  if (isLoading && !loadingTimedOut && products.length < minItemsToRender)
    return null;
  // Fetch done (or timed out) with nothing useful to show
  if (
    (!isLoading || loadingTimedOut) &&
    (error || products.length < minItemsToRender)
  )
    return null;

  return (
    <RecentlyViewedProductCardListClient
      {...props}
      title={title}
      products={products}
    />
  );
}
